import { useState, useMemo } from 'react';
import { Plus, MessageSquare, Target, Briefcase, Users, Brain, CheckCircle, Clock, BookOpen, Calendar, Bell, ExternalLink, Download, Trash2, Lightbulb } from 'lucide-react';
import { useLocation } from 'wouter';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { getCoachImage, hasCoachImage } from '../lib/coachImages';
import { getCoachProfile } from '../lib/coachProfiles';
import { getSavedMessages, saveManualGoal, updateSavedMessageMetadata, removeSavedMessage } from '../lib/api';
import { queryClient } from '../lib/queryClient';
import { buildGoogleCalendarUrl, buildICSDataUrl } from '../lib/calendar';
import { useToast } from '../hooks/useToast';

interface ActionItem {
  id: string; // uuid Database 
  messageId: string;
  title: string;
  description: string;
  status: 'todo' | 'in_progress' | 'done';
  priority: 'low' | 'medium' | 'high';
  timeframe?: string;
  dueDate?: string;
  successMetric?: string;
  category: string;
  lastCoachId?: string;
  createdAt: string;
  sessionId?: string;
  emailReminders?: boolean;
}

const WishlistPage = () => {
  const [, navigate] = useLocation();
  const { getToken } = useAuth();
  const { success: toastSuccess, error: toastError } = useToast();

  // Fetch saved messages from API
  const { data: rawMessages = [], isLoading } = useQuery({
    queryKey: ['savedMessages'],
    queryFn: async () => {
      const token = await getToken();
      return getSavedMessages(token || '');
    },
    enabled: !!getToken,
  });

  const [customGoal, setCustomGoal] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'todo' | 'in_progress' | 'done'>('all');

  // Convert raw messages to action items
  const actionItems: ActionItem[] = useMemo(() => {
    return rawMessages.map((msg: any) => ({
      id: msg.id,
      messageId: msg.messageId,
      sessionId: msg.sessionId,
      title: msg.messageContent.length > 60
        ? msg.messageContent.substring(0, 60) + '...'
        : msg.messageContent,
      description: msg.messageContent,
      status: (msg.status as 'todo' | 'in_progress' | 'done') || 'todo',
      priority: (msg.priority as 'low' | 'medium' | 'high') || 'medium',
      timeframe: msg.timeframe || '',
      dueDate: msg.dueDate || '',
      successMetric: msg.successMetric || '',
      category: msg.sessionId ? 'From Coaching' : 'Manual Entry',
      lastCoachId: msg.coachType,
      createdAt: msg.createdAt,
      emailReminders: typeof msg.emailReminders === 'boolean' ? msg.emailReminders : true
    }));
  }, [rawMessages]);

  const filteredItems = useMemo(() => {
    if (filterStatus === 'all') return actionItems;
    return actionItems.filter(item => item.status === filterStatus);
  }, [actionItems, filterStatus]);

  // Mutations
  const updateMetadataMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const token = await getToken();
      if (!token) throw new Error('No token');
      return updateSavedMessageMetadata({ id, ...updates }, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedMessages'] });
    }
  });

  const removeMutation = useMutation({
    mutationFn: async ({ id, messageId, sessionId }: { id: string; messageId: string; sessionId?: string }) => {
      const token = await getToken();
      if (!token) throw new Error('No token');
      // Pass null/undefined for manual entries, backend will handle correctly
      return removeSavedMessage(sessionId === 'manual' ? undefined : sessionId, messageId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedMessages'] });
      toastSuccess('Removed', 'Action item removed from your ledger.');
    },
    onError: (err) => {
      console.error('Remove error:', err);
      toastError('Error', 'Failed to remove message.');
    }
  });

  const handleCustomAdd = async () => {
    if (!customGoal.trim()) return;
    try {
      const token = await getToken();
      if (!token) return;
      const finalTitle = customGoal.startsWith('FOCUS AREA:') ? customGoal : `FOCUS AREA: ${customGoal}`;
      await saveManualGoal(finalTitle, 'hipo', token);
      setCustomGoal('');
      queryClient.invalidateQueries({ queryKey: ['savedMessages'] });
      toastSuccess('Goal Added', 'New focus area recorded in your ledger.');
    } catch (err) {
      toastError('Error', 'Failed to add goal.');
    }
  };

  const handleQuickAdd = async (title: string) => {
    try {
      const token = await getToken();
      if (!token) return;
      await saveManualGoal(`FOCUS AREA: ${title}`, 'hipo', token);
      queryClient.invalidateQueries({ queryKey: ['savedMessages'] });
      toastSuccess('Goal Added', `"${title}" has been added.`);
    } catch (err) {
      toastError('Error', 'Failed to add goal.');
    }
  };

  const handleUpdate = (id: string, updates: any) => {
    updateMetadataMutation.mutate({ id, updates });
  };

  const handleCoachOnThis = (item: ActionItem) => {
    // Format a detailed initial message with full context
    const initialMsg = `I need help with this focus area: ${item.description}\n\nCurrent Status: ${item.status}\nTarget Timeline: ${item.timeframe || item.dueDate || 'Not specified'}\nSuccess Metrics: ${item.successMetric || 'Not specified'}\nPriority Level: ${item.priority}\n\nI'd like guidance on how to make progress on this.`;

    console.log('🔍 PHASE 1 DEBUG - WishlistPage handleCoachOnThis:', {
      itemId: item.id,
      itemDescription: item.description,
      lastCoachId: item.lastCoachId,
      initialMessage: initialMsg,
      initialMessageLength: initialMsg.length
    });

    // Include source and item context in URL params with proper encoding
    const params = new URLSearchParams({
      source: 'action_ledger',
      itemId: item.id,
      initialMessage: encodeURIComponent(initialMsg)
    });

    // Include originating coach so Coaching can auto-select it
    if (item.lastCoachId) {
      params.set('lastCoachId', item.lastCoachId);
    }

    const finalURL = `/coaching/session?${params.toString()}`;
    console.log('🔍 PHASE 1 DEBUG - Generated URL:', finalURL);
    console.log('🔍 PHASE 1 DEBUG - URL Parameters:', Object.fromEntries(params.entries()));

    navigate(finalURL);
  };

  const handleAddToCalendar = (item: ActionItem, type: 'google' | 'ics') => {
    const event = {
      title: item.title,
      description: `Success Metric: ${item.successMetric || 'Not specified'}\n\n${item.description}`,
      start: item.dueDate ? new Date(item.dueDate) : new Date(),
      end: item.dueDate ? new Date(new Date(item.dueDate).getTime() + 3600000) : new Date(new Date().getTime() + 3600000),
    };

    if (type === 'google') {
      window.open(buildGoogleCalendarUrl(event), '_blank');
    } else {
      const link = document.createElement('a');
      link.href = buildICSDataUrl(event);
      link.download = `${item.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
      link.click();
    }
  };

  const getPriorityColor = (priority: ActionItem['priority']) => {
    switch (priority) {
      case 'high': return 'bg-red-50 text-red-700 border-red-100';
      case 'medium': return 'bg-amber-50 text-amber-700 border-amber-100';
      case 'low': return 'bg-blue-50 text-blue-700 border-blue-100';
    }
  };

  const getStatusColor = (status: ActionItem['status']) => {
    switch (status) {
      case 'done': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'in_progress': return 'bg-indigo-50 text-indigo-700 border-indigo-100';
      case 'todo': return 'bg-slate-50 text-slate-700 border-slate-100';
    }
  };

  return (
    <AppLayout currentPage="wishlist">
      <div className="min-h-screen bg-slate-50/50 pb-20">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="mb-12 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm border border-[#4C62E3]/20">
                <BookOpen className="h-7 w-7 text-[#4C62E3]" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
                  Developmental Actions Ledger
                </h1>
                <p className="text-slate-600">Track and execute your coaching-derived breakthroughs</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex rounded-xl bg-white p-1 shadow-sm border border-slate-200">
                {(['all', 'todo', 'in_progress', 'done'] as const).map((s) => {
                  const getButtonText = (status: string) => {
                    switch (status) {
                      case 'todo': return 'to do';
                      case 'in_progress': return 'in progress';
                      default: return status;
                    }
                  };

                  return (
                    <button
                      key={s}
                      onClick={() => setFilterStatus(s)}
                      className={`px-4 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-lg transition-all ${filterStatus === s
                        ? 'bg-royal-blue !text-white shadow-sm hover:bg-royal-blue/90 hover:text-white'
                        : 'text-slate-500 hover:text-white hover:bg-royal-blue/90'
                        }`}
                    >
                      {getButtonText(s)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Quick Add */}
          <div className="mb-12 rounded-3xl bg-white border border-slate-200 p-8 shadow-sm">
            <h2 className="mb-6 font-display text-xl font-bold text-slate-900">Add New Action Area(s)</h2>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      value={customGoal}
                      onChange={(e) => setCustomGoal(e.target.value)}
                      placeholder="e.g. Implement daily stand-ups for better team alignment"
                      className="h-12 rounded-xl border-slate-200 pl-4 font-body focus:ring-royal-blue"
                      onKeyPress={(e) => e.key === 'Enter' && handleCustomAdd()}
                    />
                  </div>
                  <Button
                    onClick={handleCustomAdd}
                    disabled={!customGoal.trim()}
                    className="h-12 rounded-xl bg-royal-blue px-6 font-display font-bold !text-white hover:bg-royal-blue/90 disabled:text-white"
                  >
                    <Plus className="mr-2 h-5 w-5" />
                    Record Item
                  </Button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['Practice active listening', 'Set quarterly KPIs', 'Review team structure'].map(suggest => (
                    <button
                      key={suggest}
                      onClick={() => handleQuickAdd(suggest)}
                      className="rounded-lg bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-royal-blue/10 hover:text-royal-blue"
                    >
                      + {suggest}
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl bg-amber-50 p-6 border border-amber-100">
                <div className="flex gap-3">
                  <Lightbulb className="h-5 w-5 text-amber-600 shrink-0" />
                  <div>
                    <h3 className="text-sm font-bold text-amber-900 mb-1">Coach's Tip</h3>
                    <p className="text-xs text-amber-800 leading-relaxed">
                      Effective action items start with a verb and include a specific success metric.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* List */}
          <div className="grid gap-6">
            {isLoading ? (
              <div className="flex h-40 items-center justify-center rounded-3xl bg-white border border-slate-200 py-12">
                <Clock className="h-8 w-8 animate-spin text-royal-blue" />
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="rounded-3xl border-2 border-dashed border-slate-200 bg-white p-12 text-center">
                <Target className="mx-auto mb-4 h-12 w-12 text-slate-300" />
                <h3 className="font-display text-lg font-bold text-slate-900">No active focus areas</h3>
                <p className="text-slate-500">Your Action Ledger is empty. Record insights from your coaching sessions here.</p>
              </div>
            ) : (
              filteredItems.map((item) => {
                const CoachProfile = item.lastCoachId ? getCoachProfile(item.lastCoachId) : null;
                const StatusIcon = item.status === 'done' ? CheckCircle : (item.status === 'in_progress' ? Clock : Target);

                return (
                  <Card key={item.id} className="overflow-hidden bg-white rounded-3xl border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 group">
                    <CardContent className="p-0">
                      <div className="flex flex-col lg:flex-row">
                        {/* Status Sidebar */}
                        <div className={`w-full lg:w-32 flex lg:flex-col items-center justify-center p-4 gap-4 border-b lg:border-b-0 lg:border-r border-slate-100 ${getStatusColor(item.status)}`}>
                          <StatusIcon className="h-6 w-6" />
                          <span className="text-[10px] font-display font-extrabold uppercase tracking-widest text-center">{item.status.replace('_', ' ')}</span>

                          <select
                            value={item.status}
                            onChange={(e) => handleUpdate(item.id, { status: e.target.value })}
                            className="hidden group-hover:block lg:mt-2 w-full text-[10px] bg-white rounded border-slate-300 text-slate-900 focus:ring-royal-blue"
                            aria-label="Update item status"
                            title="Update item status"
                          >
                            <option value="todo">Todo</option>
                            <option value="in_progress">In Progress</option>
                            <option value="done">Done</option>
                          </select>
                        </div>

                        {/* Main Content */}
                        <div className="flex-1 p-6 sm:p-8">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-3 mb-2">
                                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${getPriorityColor(item.priority)}`}>
                                  {item.priority} Priority
                                </span>
                                {item.timeframe && (
                                  <span className="flex items-center gap-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                    <Clock className="h-3 w-3" /> {item.timeframe}
                                  </span>
                                )}
                              </div>
                              <h3 className="font-display text-xl font-bold text-slate-900 group-hover:text-royal-blue transition-colors mb-2">
                                {item.title}
                              </h3>
                              <p className="font-body text-sm text-slate-600 leading-relaxed max-w-2xl">
                                {item.description}
                              </p>
                            </div>

                            <div className="flex shrink-0 gap-2">
                              {CoachProfile && (
                                <div className="flex items-center gap-2 rounded-2xl bg-slate-50 border border-slate-100 pr-4 pl-1 py-1" title={`Insights from ${CoachProfile.name}`}>
                                  <div className={`h-8 w-8 rounded-full overflow-hidden bg-gradient-to-br ${CoachProfile.color}`}>
                                    {hasCoachImage(CoachProfile.id) ? (
                                      <img
                                        src={getCoachImage(CoachProfile.id)}
                                        className="h-full w-full object-cover"
                                        alt={`${CoachProfile.name}'s profile picture`}
                                        title={`${CoachProfile.name}'s profile picture`}
                                      />
                                    ) : (
                                      <span className="flex items-center justify-center h-full text-xs font-bold text-white">{CoachProfile.name[0]}</span>
                                    )}
                                  </div>
                                  <span className="text-xs font-bold text-slate-800">{CoachProfile.name}</span>
                                </div>
                              )}

                              <Button
                                onClick={() => handleCoachOnThis(item)}
                                className="h-10 rounded-xl bg-royal-blue px-4 font-display font-bold !text-white hover:bg-royal-blue/90 disabled:text-white"
                              >
                                <MessageSquare className="mr-2 h-4 w-4" />
                                Coach on This
                              </Button>
                            </div>
                          </div>

                          {/* Metadata Fields */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-6 border-t border-slate-50">
                            <div>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Success Metric</p>
                              <div className="relative">
                                <Input
                                  defaultValue={item.successMetric}
                                  onBlur={(e) => handleUpdate(item.id, { successMetric: e.target.value })}
                                  placeholder="What does 'done' look like?"
                                  className="h-9 text-xs rounded-lg border-transparent hover:border-slate-200 focus:border-royal-blue bg-slate-50/50"
                                />
                                <CheckCircle className="absolute right-3 top-2.5 h-3.5 w-3.5 text-slate-300" />
                              </div>
                            </div>

                            <div>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Due Date / Reminder</p>
                              <div className="relative">
                                <Input
                                  type="date"
                                  defaultValue={item.dueDate ? item.dueDate.split('T')[0] : ''}
                                  onChange={(e) => handleUpdate(item.id, { dueDate: e.target.value })}
                                  className="h-9 text-xs rounded-lg border-transparent hover:border-slate-200 focus:border-royal-blue bg-slate-50/50"
                                />
                                <Calendar className="absolute right-3 top-2.5 h-3.5 w-3.5 text-slate-300 pointer-events-none" />
                              </div>
                            </div>

                            <div className="flex items-end justify-between gap-4">
                              <div className="flex-1">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Priority</p>
                                <select
                                  value={item.priority}
                                  onChange={(e) => handleUpdate(item.id, { priority: e.target.value })}
                                  className="w-full h-9 text-xs rounded-lg border-transparent bg-slate-50/50 hover:border-slate-200 focus:ring-royal-blue"
                                  aria-label="Update item priority"
                                  title="Update item priority"
                                >
                                  <option value="low">Low</option>
                                  <option value="medium">Medium</option>
                                  <option value="high">High</option>
                                </select>
                              </div>

                              <div className="flex gap-2">
                                <label className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700 border border-slate-200">
                                  <input
                                    type="checkbox"
                                    checked={item.emailReminders !== false}
                                    onChange={(e) => handleUpdate(item.id, { emailReminders: e.target.checked })}
                                  />
                                  Email follow-ups
                                </label>
                                <div className="relative group/cal">
                                  <Button size="icon" variant="ghost" className="h-9 w-9 rounded-lg hover:bg-slate-100 text-slate-500">
                                    <Calendar className="h-4 w-4" />
                                  </Button>
                                  <div className="absolute bottom-full right-0 mb-2 invisible group-hover/cal:visible bg-white border border-slate-200 rounded-xl shadow-xl p-2 z-10 w-40">
                                    <button onClick={() => handleAddToCalendar(item, 'google')} className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 rounded-lg">
                                      <ExternalLink className="h-3 w-3" /> Google Calendar
                                    </button>
                                    <button onClick={() => handleAddToCalendar(item, 'ics')} className="flex w-full items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 rounded-lg">
                                      <Download className="h-3 w-3" /> Download .ics
                                    </button>
                                  </div>
                                </div>

                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => removeMutation.mutate({ id: item.id, messageId: item.messageId, sessionId: item.sessionId || 'manual' })}
                                  className="h-9 w-9 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default WishlistPage;
